import React, { useEffect, useRef, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { formatTime, signalRestEnd } from '../lib/workoutPlayer';

// Floating rest countdown for the training journal. Started by the client
// after a set with the plan's rest time; can be extended or ended early.
// Counts from a fixed end timestamp, so a locked phone or background tab
// doesn't make it drift.
export const RestTimer = ({ timer, onExtend, onClose }) => {
  const [now, setNow] = useState(Date.now());
  const signalled = useRef(null);

  useEffect(() => {
    if (!timer) return undefined;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [timer]);

  const left = timer ? timer.endsAt - now : 0;
  const finished = timer && left <= 0;

  useEffect(() => {
    if (finished && signalled.current !== timer.endsAt) {
      signalled.current = timer.endsAt;
      signalRestEnd();
    }
  }, [finished, timer]);

  // Let the "time for the next set" message stay a moment, then hide.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!finished) return undefined;
    const id = setTimeout(() => closeRef.current(), 15000);
    return () => clearTimeout(id);
  }, [finished]);

  if (!timer) return null;
  const pct = Math.max(0, Math.min(100, (left / timer.total) * 100));

  return (
    <div className="fixed left-4 right-4 bottom-24 sm:bottom-6 z-40 max-w-xl mx-auto glass glass-float rounded-[26px] p-4 flex flex-col gap-3 shadow-2xl"
      role="timer" aria-live="polite" data-testid="rest-timer">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="label-text text-[var(--text-muted)] text-[0.55rem]">{finished ? 'Koniec przerwy' : 'Przerwa'}</p>
          <p className="text-xs text-[var(--text-muted)] truncate">{finished ? `Czas na ${timer.next}` : timer.label}</p>
        </div>
        <span className={`big-num text-4xl tabular-nums ${finished || left <= 5000 ? 'text-[#C9A84C]' : ''}`}>
          {finished ? '0:00' : formatTime(left)}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-[var(--track)] overflow-hidden">
        <div className="h-full bg-[#C9A84C] transition-[width] duration-200" style={{ width: `${pct}%` }} />
      </div>
      <div className="flex gap-2">
        <button type="button" onClick={() => onExtend(15)} className="cta-ghost flex-1 h-10 rounded-full text-xs font-semibold flex items-center justify-center gap-1" data-testid="rest-plus-15">
          <Plus className="w-3.5 h-3.5" /> 15 s
        </button>
        <button type="button" onClick={() => onExtend(30)} className="cta-ghost flex-1 h-10 rounded-full text-xs font-semibold flex items-center justify-center gap-1" data-testid="rest-plus-30">
          <Plus className="w-3.5 h-3.5" /> 30 s
        </button>
        <button type="button" onClick={onClose} className="cta-primary flex-1 h-10 rounded-full text-xs font-semibold flex items-center justify-center gap-1" data-testid="rest-close">
          <X className="w-3.5 h-3.5" /> {finished ? 'OK' : 'Pomiń'}
        </button>
      </div>
    </div>
  );
};
